import * as Sentry from "@sentry/react";
import type { BrowserOptions } from "@sentry/react";
import { useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";

import { shouldIgnoreApiError } from "./utils/apiErrorReporting";
import { isExpectedBusinessResponse } from "./utils/expectedBusinessResponses";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENVIRONMENT =
  import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;
const APP_VERSION = import.meta.env.VITE_APP_VERSION || "0.0.0";
const IS_PRODUCTION = import.meta.env.PROD;

/**
 * Configuracion completa del SDK, exportada como valor para poder probarla.
 *
 * El fallo que arreglamos aqui solo se ve montando un cliente REAL con estas
 * mismas opciones: los filtros de Sentry no corren todos en el mismo sitio, asi
 * que un helper probado por separado puede estar en lo cierto mientras el
 * camino real descarta el evento. Exportarlas es lo que permite que el test
 * atraviese la cadena entera (integraciones + beforeSend) en vez de comprobar
 * una pieza suelta.
 */
export const sentryOptions: BrowserOptions = {
  dsn: SENTRY_DSN,
  environment: SENTRY_ENVIRONMENT,
  release: `mb-app-express@${APP_VERSION}`,
  enabled: IS_PRODUCTION && !!SENTRY_DSN,

  // Enable Sentry Logs for cross-session analysis and backend correlation
  _experiments: {
    enableLogs: true,
  },

  integrations: [
    // React Router v6 browser tracing integration for better route parameterization
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.browserProfilingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,

      // Unmask safe UI chrome elements (navigation, buttons, labels, etc.)
      // These contain only static UI text, no PII
      unmask: [
        // Explicit unmask attribute
        "[data-sentry-unmask]",
        ".sentry-unmask",

        // Navigation & Layout
        "[data-slot='breadcrumb']",
        "[data-slot='breadcrumb-list']",
        "[data-slot='breadcrumb-item']",
        "[data-slot='breadcrumb-link']",
        "[data-slot='breadcrumb-page']",
        "[data-slot='breadcrumb-separator']",
        "nav[aria-label='breadcrumb']",
        ".nav-section-label", // Section headers in navigation

        // Buttons & Actions (text labels only)
        "[data-slot='button']",
        "[data-slot='dropdown-menu-item']",
        "[data-slot='dropdown-menu-label']",
        "[data-slot='dropdown-menu-trigger']",

        // Tabs & Steps
        "[role='tab']",
        "[role='tablist']",
        ".step-title", // Step indicator titles

        // Badges & Status indicators
        "[data-slot='badge']",

        // Table headers (column names, not data)
        "th",
        ".ant-table-thead",
        ".ant-table-column-title",

        // Tooltips (helper text)
        "[data-slot='tooltip-content']",

        // Empty states & Loading messages
        ".ant-empty-description",
        ".ant-result-title",
        ".ant-result-subtitle",

        // Page structure
        ".ant-page-header-heading-title",
        ".ant-card-head-title",

        // Ant Design Menu items
        ".ant-menu-title-content",
        ".ant-menu-item",
        ".ant-menu-submenu-title",

        // Pagination
        ".ant-pagination-item",
        ".ant-pagination-prev",
        ".ant-pagination-next",
      ],

      // Mask sensitive content - use data-sentry-mask attribute in components
      mask: [
        "[data-sentry-mask]",
        ".sentry-mask",
        ".user-pii", // User PII display (name, email in header)
      ],

      // Block elements from being recorded entirely
      block: [
        ".sigCanvas", // Signature canvas
        ".react-pdf__Document", // PDF document viewer
        ".react-pdf__Page", // PDF pages
        "[data-sentry-block]",
      ],

      // Ignore input values (never captured)
      // IMPORTANT: These patterns protect sensitive data from being recorded in replays
      ignore: [
        // Standard sensitive input types
        'input[type="password"]',
        'input[type="email"]',
        'input[type="tel"]',

        // Payment information (AddCardModal)
        'input[name*="card"]',
        'input[name*="cvv"]',
        'input[name*="expir"]', // expiration_month, expiration_year
        'input[name*="holder"]', // holder_name

        // Mexican identifiers (PersonForm, TenantRegistration)
        'input[name*="curp"]',
        'input[name*="rfc"]',
        'input[name*="clabe"]', // RentDepositAndDatesForm
        'input[name*="identification"]', // identification_number (ID numbers)

        // Personal information
        'input[name*="name"]', // Person names (name, second_name, legal_entity_name)
        'input[name*="surname"]', // first_surname, second_surname
        'input[name*="phone"]',
        'input[name*="email"]',
        'input[name*="birth"]', // birth_date, birth_country, birth_city, birth_state

        // Address information (AddressForm)
        'input[name*="street"]',
        'input[name*="address"]', // Catches address-street, address-zip_code, etc.
        'input[name*="zip"]', // zip_code
        'input[name*="neighborhood"]',

        // API credentials (BuroDeCredito, organization config)
        'input[name*="buro"]', // buro_de_credito_* fields
        'input[name*="password"]', // Catches password fields without type="password"
        'input[name*="user"]', // API usernames
        'input[name*="account"]',
        'input[name*="credential"]',

        // Textareas can contain sensitive data (full addresses, notes)
        "textarea",

        // Explicit ignore attribute
        "[data-sentry-ignore]",
      ],

      networkRequestHeaders: ["Content-Type", "Accept"],
      networkResponseHeaders: ["Content-Type", "X-Request-Id"],
    }),
  ],

  // Performance Monitoring - sample 10% of transactions in production
  tracesSampleRate: IS_PRODUCTION ? 0.1 : 1.0,

  // Profiling - sample rate for continuous profiling (v10.27.0+)
  profileSessionSampleRate: IS_PRODUCTION ? 0.1 : 1.0,

  // Session Replay - only capture sessions with errors (no regular session sampling)
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,

  // Track API calls to your backend (distributed tracing)
  tracePropagationTargets: [
    /^\//,                                // Relative URLs (same origin)
    /^https?:\/\/.*\.multiburo\.com\.mx/, // *.multiburo.com.mx
  ],

  // Increase depth for Redux state serialization
  normalizeDepth: 5,

  // Filter sensitive data before sending to Sentry
  beforeSend(event, hint) {
    const error = hint?.originalException as Error & {
      response?: { status?: number };
      status?: number;
    };

    // Abortos del cliente, fallos de transporte y el `timeout: 0`: se descartan
    // AQUI, no en `ignoreErrors`, porque este es el primer filtro que recibe el
    // error original y no solo su texto.
    //
    // El orden real de @sentry/core es: los `processEvent` de las integraciones
    // (EventFilters, que es quien implementa `ignoreErrors`) corren dentro de
    // `_prepareEvent`, y solo despues se llama a `beforeSend`. Es decir, lo que
    // `ignoreErrors` descarta no llega nunca hasta aqui: no hay forma de
    // "repescarlo" mas abajo.
    //
    // Por eso tener los patrones de aborto en `ignoreErrors` contradecia la
    // garantia de `shouldIgnoreApiError`: una respuesta HTTP real significa que
    // la peticion se completo y el error no debe descartarse por coincidencia
    // de texto, pero un 500 cuyo mensaje dijera "Request aborted" moria en el
    // filtro de mensaje, que no sabe nada del status. `shouldIgnoreApiError` mira
    // el objeto (code, `response`) y solo cae al mensaje cuando no hay respuesta,
    // que es justo la informacion que le faltaba al filtro global.
    if (shouldIgnoreApiError(hint?.originalException)) {
      return null;
    }

    // Red de seguridad de la tabla de respuestas de negocio esperadas
    // (`expectedBusinessResponses.ts`). Las dos puertas de captura —el
    // interceptor de axios y los caches de react-query— ya la aplican, asi que
    // en el camino normal el evento no llega hasta aqui; esto cubre cualquier
    // otro camino a Sentry (un error boundary, un `unhandledrejection` de una
    // promesa sin dueño) sin que la regla haya que escribirla dos veces.
    //
    // Devolver `null` aqui no cuesta nada: se comprobo en
    // @sentry/core/build/cjs/client.js que `sendEvent` —y con el el hook
    // `beforeSendEvent`, del que cuelga la subida de replay— corre DESPUES de
    // `beforeSend`, asi que la cadena se corta antes de cualquier envio.
    //
    // `isExpectedBusinessResponse` no puede lanzar (try/catch interno con
    // default "capturar"), y eso es critico justo aqui: @sentry/core atrapa un
    // fallo de `beforeSend`, lo registra como error interno y DESCARTA el
    // evento, asi que un TypeError en una regla no abriria un issue, apagaria
    // TODOS en silencio.
    if (isExpectedBusinessResponse(hint?.originalException)) {
      return null;
    }

    // Ignore 403 Forbidden errors (auth/permission issues handled by app)
    if (
      error?.response?.status === 403 ||
      error?.status === 403 ||
      event.exception?.values?.some((e) => e.value?.includes("403"))
    ) {
      return null;
    }

    // Ignore 400/401 on unauthenticated auth routes — these are expected user
    // input errors (bad password, invalid signup data, expired verify token)
    // and not actionable bugs. Were saturating dashboards (~2200 events/14d).
    const status = error?.response?.status ?? error?.status;
    const requestUrl = event.request?.url ?? "";
    const AUTH_ROUTES = [
      "/login",
      "/signup",
      "/verify-account",
      "/forgot-password",
      "/reset-password",
      "/change-password",
    ];
    if (
      (status === 400 || status === 401) &&
      AUTH_ROUTES.some((p) => requestUrl.includes(p))
    ) {
      return null;
    }

    const sensitivePatterns = [
      // Auth & Security
      "token",
      "password",
      "secret",
      "auth",
      "credential",
      // Payment
      "card",
      "cvv",
      "credit",
      "expir",
      "holder",
      // Mexican identifiers
      "curp",
      "rfc",
      "clabe",
      "identification",
      // Personal info
      "account_number",
      "phone",
      "email",
      "birth",
      // Address
      "street",
      "zip_code",
      "neighborhood",
      // API credentials
      "buro",
    ];

    // Remove sensitive data from breadcrumbs
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
        const messageContainsSensitive = sensitivePatterns.some(
          (p) =>
            breadcrumb.message?.toLowerCase().includes(p) ||
            JSON.stringify(breadcrumb.data || {})
              .toLowerCase()
              .includes(p),
        );

        if (messageContainsSensitive) {
          return {
            ...breadcrumb,
            message: "[Filtered]",
            data: {},
          };
        }
        return breadcrumb;
      });
    }

    // Remove sensitive headers from request data
    if (event.request?.headers) {
      const sensitiveHeaders = [
        "Authorization",
        "authorization",
        "Cookie",
        "cookie",
        "X-Auth-Token",
        "x-auth-token",
      ];
      sensitiveHeaders.forEach((h) => {
        delete event.request?.headers?.[h];
      });
    }

    // Remove sensitive cookies
    if (event.request?.cookies) {
      const filteredCookies = { ...event.request.cookies };
      delete filteredCookies.session;
      delete filteredCookies.token;
      delete filteredCookies.userToken;
      event.request.cookies = filteredCookies;
    }

    // Filter sensitive request body data (handle both strings and objects)
    if (event.request?.data) {
      const dataStr =
        typeof event.request.data === "string"
          ? event.request.data
          : JSON.stringify(event.request.data);

      const bodyContainsSensitive = sensitivePatterns.some((p) =>
        dataStr.toLowerCase().includes(p),
      );

      if (bodyContainsSensitive) {
        event.request.data = "[Filtered]";
      }
    }

    return event;
  },

  // Ignore common non-actionable errors
  ignoreErrors: [
    // Network errors
    "Network Error",
    "Failed to fetch",
    "Load failed",
    "NetworkError when attempting to fetch resource",
    "The request timed out",
    // HTTP 403 Forbidden (usually auth/permission issues handled by app)
    /Request failed with status code 403/,
    /status code 403/i,
    /403 Forbidden/i,
    // Los abortos del cliente y el `timeout: 0` NO se listan aqui a proposito:
    // se deciden en `beforeSend` con `shouldIgnoreApiError`, que es el unico
    // filtro que ve el objeto de error y puede distinguir un aborto de verdad
    // de un 5xx que solo lo parece por el texto del mensaje.
    //
    // Del resto de la lista, todo lo que va por debajo de este comentario son
    // cadenas del navegador o de librerias que nunca conviven con una
    // respuesta HTTP, asi que filtrarlas por mensaje no esconde ningun status.
    //
    // La excepcion son los tres patrones de 403 de aqui arriba, que SI son el
    // texto de una respuesta HTTP: se quedan a proposito, porque `beforeSend`
    // descarta el 403 de todos modos unas lineas mas abajo y adelantarlo aqui
    // ahorra construir el evento. Se distinguen del caso del aborto en que el
    // mensaje nombra el status en vez de contradecirlo.
    // Browser extensions and third-party scripts
    "top.GLOBALS",
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /^safari-extension:\/\//,
    // Script errors from third-parties
    /^Script error\.?$/,
    /^Javascript error: Script error\.? on line 0$/,
    // ResizeObserver errors (browser quirk)
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    // PDF.js errors
    "Setting up fake worker",
    "Worker was destroyed",
    // Common Ant Design errors
    "Cannot read properties of null (reading 'getBoundingClientRect')",
  ],

  // Block errors from specific URLs
  denyUrls: [
    // Browser extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
    // Third-party analytics and tracking
    /google-analytics\.com/i,
    /googletagmanager\.com/i,
    /hotjar\.com/i,
    /intercom\.io/i,
    /cdn\.segment\.com/i,
    /facebook\.net/i,
  ],
};

Sentry.init(sentryOptions);

export default Sentry;
